import MuiCard from '@mui/material/Card';
import MuiCardContent from '@mui/material/CardContent';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import React from 'react';

const PREFIX = 'DetailsCard';

const classes = {
  cardMui: `${PREFIX}-cardMui`,
  details: `${PREFIX}-details`,
  detailsTitle: `${PREFIX}-detailsTitle`,
  subDetailTitle: `${PREFIX}-subDetailTitle`,
  donutDiv: `${PREFIX}-donutDiv`,
  performance: `${PREFIX}-performance`,
  SVGDonutInnerCircle: `${PREFIX}-SVGDonutInnerCircle`,
  SVGDonutValueStrokeCircle: `${PREFIX}-SVGDonutValueStrokeCircle`,
  actualTitle: `${PREFIX}-actualTitle`,
  actualValue: `${PREFIX}-actualValue`,
  linkStyle: `${PREFIX}-linkStyle`,
  targetValue: `${PREFIX}-targetValue`
};

const StyledMuiCard = styled(MuiCard)(({ theme }) => ({
  [`&.${classes.cardMui}`]: {
    display: 'flex',
    height: '280px'
  },

  [`& .${classes.details}`]: {
    position: 'relative',
    width: '100%',
    padding: theme.spacing(1)
  },

  [`& .${classes.detailsTitle}`]: {
    color: theme.palette.text.primary
  },

  [`& .${classes.subDetailTitle}`]: {
    whiteSpace: 'pre-line',
    color: theme.palette.text.secondary
  },

  [`& .${classes.donutDiv}`]: {
    position: 'absolute',
    bottom: theme.spacing(4),
    right: theme.spacing(4),
    width: '250px'
  },

  [`& .${classes.performance}`]: {
    height: '180px'
  },

  [`& .${classes.SVGDonutInnerCircle}`]: {
    fill: 'none'
  },

  [`& .${classes.SVGDonutValueStrokeCircle}`]: {
    fill: 'none',
    strokeWidth: '2px',
    strokeLinecap: 'round',
    cx: '21',
    cy: '21',
    r: '16'
  },

  [`& .${classes.actualTitle}`]: {
    fill: theme.palette.text.secondary,
    fontSize: '4px',
    fontWeight: 'normal',
    x: '90%',
    y: '40%',
    textAnchor: 'middle'
  },

  [`& .${classes.actualValue}`]: {
    fill: theme.palette.text.primary,
    fontSize: '6px',
    fontWeight: 'bold',
    x: '90%',
    y: '61.5%',
    textAnchor: 'middle'
  },

  [`& .${classes.linkStyle}`]: {
    textDecoration: 'none',
    color: theme.palette.text.primary
  },

  [`& .${classes.targetValue}`]: {
    color: theme.palette.text.primary
  }
}));

interface Data {
  name?: string;
  rangeColor?: string;
  prettyActual?: string;
  prettyTarget?: string;
}

interface DetailsCardItem {
  title?: string;
  actualLabel?: string;
  targetLabel?: string;
  periodLabel?: string;
  periodName?: string;
  data?: Data;
}

const DetailsCard = ({
  title = 'Details',
  actualLabel = 'Actual',
  targetLabel = 'Target',
  periodLabel = 'Period',
  periodName = 'sumthin',
  data
}: DetailsCardItem) => {
  return (
    <StyledMuiCard raised className={classes.cardMui}>
      <div className={classes.details} data-testid="KPIDetailsCard-content">
        <MuiCardContent>
          <Typography
            variant="h5"
            className={classes.detailsTitle}
            data-testid="KPIDetailsCard-title"
          >
            {title}
          </Typography>
          <Typography variant="body2" className={classes.subDetailTitle}>
            {'\n'}
            {targetLabel}
          </Typography>
          <Typography
            className={classes.targetValue}
            variant="h6"
            data-testid="KPIDetailsCard-target"
          >
            {data?.prettyTarget || ''}
          </Typography>
          <Typography variant="body2" className={classes.subDetailTitle}>
            {'\n'}
            {periodLabel}
          </Typography>
          <Typography
            className={classes.targetValue}
            variant="h6"
            data-testid="KPIDetailsCard-period"
          >
            {periodName}
          </Typography>
        </MuiCardContent>
        <div className={classes.donutDiv} data-testid="KPIDetailsCard-donut">
          <svg width="100%" height="100%" viewBox="0 0 23 42" className={classes.performance}>
            <circle
              data-testid="KPIDetailsCard-colorRing"
              className={classes.SVGDonutValueStrokeCircle}
              stroke={`#${data?.rangeColor || '000000'}`}
              strokeDasharray="100"
              cx="21"
              cy="21"
              r="16"
            />
            <text>
              <tspan
                className={classes.actualTitle}
                width="100"
                height="40"
                x="90%"
                y="40%"
                textAnchor="middle"
              >
                {actualLabel}
              </tspan>
            </text>
            <text>
              <tspan
                id="actualID"
                className={classes.actualValue}
                width="100"
                height="40"
                x="90%"
                y="61.5%"
                textAnchor="middle"
                data-testid="KPIDetailsCard-actual"
              >
                {data?.prettyActual || ''}
              </tspan>
            </text>
          </svg>
        </div>
      </div>
    </StyledMuiCard>
  );
};

export default DetailsCard;
